import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ASSISTANT_MODES, STORAGE_KEYS } from './config/modes';
import { Storage } from '../../constants/storage';
import { useAssistantMode } from './hooks/useAssistantMode';
import AssistantShell from './AssistantShell';
import PageHeader from './shell/PageHeader';
import ChatApp from '../ChatApp';
import SimplizeLogo from '../../components/SimplizeLogo';
import DockPill from './shell/DockPill';

const readStoredIsOpen = () => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEYS.IS_OPEN);
        if (raw === null) return null;
        return JSON.parse(raw) === true;
    } catch {
        return null;
    }
};

const readStoredMode = () => {
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage.getItem(STORAGE_KEYS.MODE);
    } catch {
        return null;
    }
};

const writeStoredIsOpen = (value) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEYS.IS_OPEN, JSON.stringify(!!value));
    } catch {
        // ignore
    }
};

const readStoredUserId = () => {
    if (typeof window === 'undefined') return 'guest';
    try {
        const raw = window.localStorage.getItem(Storage.current_user);
        if (raw) {
            const user = JSON.parse(raw);
            if (user && user.id) return String(user.id);
        }
    } catch {
        // ignore
    }
    try {
        return window.localStorage.getItem(Storage.super_agent_user_id) || 'guest';
    } catch {
        return 'guest';
    }
};

function FabTrigger({ onClick, label = 'Open assistant' }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className="fixed bottom-6 right-6 z-20 w-14 h-14 rounded-full bg-white hover:bg-gray-50 border border-gray-200 shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        >
            <SimplizeLogo className="h-5 w-auto" iconOnly />
        </button>
    );
}

export default function ChatAssistant({
    embedded = true,
    mode: controlledMode,
    defaultMode = ASSISTANT_MODES.FLOATING,
    onModeChange,
    defaultOpen = false,
    onOpenChange,
    title = 'Simplize Assistant',
    side = 'right',
    persistMode = true,
    persistOpen = true,
    triggerLabel,
    showTriggerWhenClosed = true,
    fullscreenTarget = '/chat',
    noCache = false,
    cacheKey,
}) {
    if (!embedded) {
        return (
            <>
                <PageHeader />
                <ChatApp />
            </>
        );
    }

    return (
        <EmbeddedAssistant
            controlledMode={controlledMode}
            defaultMode={defaultMode}
            onModeChange={onModeChange}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
            title={title}
            side={side}
            persistMode={persistMode}
            persistOpen={persistOpen}
            triggerLabel={triggerLabel}
            showTriggerWhenClosed={showTriggerWhenClosed}
            fullscreenTarget={fullscreenTarget}
            noCache={noCache}
            cacheKey={cacheKey}
        />
    );
}

function EmbeddedAssistant({
    controlledMode,
    defaultMode,
    onModeChange,
    defaultOpen,
    onOpenChange,
    title,
    side,
    persistMode,
    persistOpen,
    triggerLabel,
    showTriggerWhenClosed,
    fullscreenTarget,
    noCache,
    cacheKey,
}) {
    const location = useLocation();
    const userId = readStoredUserId();
    
    const effectiveCacheKey = cacheKey;

    const resetKey = useMemo(() => {
        const baseKey = cacheKey ?? (noCache ? location.pathname : undefined);
        if (!baseKey) return undefined;
        return `${userId}:${baseKey}`;
    }, [userId, cacheKey, noCache, location.pathname]);
    const [isOpen, setIsOpen] = useState(() => {
        const storedMode = readStoredMode();
        if (storedMode === ASSISTANT_MODES.DOCK) return false;
        if (persistOpen) {
            const stored = readStoredIsOpen();
            if (stored !== null) return stored;
        }
        return defaultOpen;
    });
    const [mode, setMode] = useAssistantMode({
        initialMode: defaultMode === ASSISTANT_MODES.FULLSCREEN ? ASSISTANT_MODES.FLOATING : defaultMode,
        controlledMode: controlledMode === ASSISTANT_MODES.FULLSCREEN ? undefined : controlledMode,
        onModeChange,
        persist: persistMode,
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (persistOpen) writeStoredIsOpen(isOpen);
    }, [isOpen, persistOpen]);

    const handleOpen = useCallback(() => {
        setIsOpen(true);
        onOpenChange?.(true);
    }, [onOpenChange]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        onOpenChange?.(false);
    }, [onOpenChange]);

    useEffect(() => {
        if (mode !== ASSISTANT_MODES.DOCK) return undefined;
        if (isOpen) return undefined;
        const handler = (e) => {
            if (!e.shiftKey) return;
            if (e.key !== '?' && e.key !== '/') return;
            const target = e.target;
            if (
                target && (
                    target.tagName === 'INPUT'
                    || target.tagName === 'TEXTAREA'
                    || target.isContentEditable
                )
            ) return;
            e.preventDefault();
            handleOpen();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [mode, isOpen, handleOpen]);

    const handleModeChange = useCallback(
        (next) => {
            if (next === ASSISTANT_MODES.FULLSCREEN) {
                let lastSessionId = null;
                try {
                    lastSessionId = window.localStorage.getItem(STORAGE_KEYS.LAST_SESSION);
                } catch {
                    lastSessionId = null;
                }
                if (typeof fullscreenTarget === 'function') {
                    fullscreenTarget(lastSessionId);
                    return;
                }
                const target = lastSessionId ? `/chat/${lastSessionId}` : fullscreenTarget;
                navigate(target);
                return;
            }
            setMode(next);
        },
        [fullscreenTarget, navigate, setMode],
    );

    if (mode === ASSISTANT_MODES.FRAMLESS) {
        return (
            <AssistantShell
                mode={mode}
                onModeChange={handleModeChange}
                onClose={undefined}
                title={title}
                side={side}
                resetKey={resetKey}
                cacheKey={effectiveCacheKey}
            />
        );
    }

    if (!isOpen) {
        if (!showTriggerWhenClosed) return null;
        if (mode === ASSISTANT_MODES.DOCK) {
            return <DockPill onClick={handleOpen} label={triggerLabel || 'Hỏi Nebula'} />;
        }
        return <FabTrigger onClick={handleOpen} label={triggerLabel} />;
    }

    return (
        <AssistantShell
            mode={mode}
            onModeChange={handleModeChange}
            onClose={handleClose}
            title={title}
            side={side}
            resetKey={resetKey}
            cacheKey={effectiveCacheKey}
        />
    );
}
